import { Box, Typography, CircularProgress } from "@mui/material";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import "./DataTable.css";
import DynamicTable from "../../../utils/Table/DynamicTable";
import {
  capitalizeFirstLetter,
  truncateArray,
  TitleWithTooltip,
} from "../../../utils/function";
import { CURATOR_PAGE_API_CONSTANT } from "../../../constant/constant";
import ActionModal from "../ActionModal";
import { Icon } from "pepsico-ds";

const DataTable = ({
  pageName,
  Tabledata,
  serachText,
  loading,
  refreshData,
}) => {
  const navigate = useNavigate();
  const [isModalOpen, setModalOpen] = useState(false);
  const [modalType, setModalType] = useState("");
  const [selectedRow, setSelectedRow] = useState({});

  const handleActionClick = (row, action) => {
    setSelectedRow(row);
    setModalType(action);
    setModalOpen(true);
  };

  const handleNavigate = (item) => {
    if (pageName === CURATOR_PAGE_API_CONSTANT.artificat_history) {
      navigate(`/curator/artifact-history/${item?.contentGuid}`);
    } else if (pageName === CURATOR_PAGE_API_CONSTANT.denied_history) {
      navigate(`/curator/denied-artificats-history/${item?.contentGuid}`);
    }
  };

  const processedRows = Tabledata?.map((row) => ({
    ...row,
    type: capitalizeFirstLetter(row.type),
    status: capitalizeFirstLetter(row.status),
    truncateTitle: row.title,
    fullTitle: row.title, // Store full title for tooltip
    truncatedSector: truncateArray(row.sector.split(",")),
    truncatedCountry: truncateArray(row.country.split(",")),
    truncatedSubject: truncateArray(row.subject.split(",")),
    fullSector: row.sector, // Store full values separately
    fullCountry: row.country,
    fullSubject: row.subject,
  }));
  const baseColumns = [
    {
      id: "truncateTitle",
      label: "CONTENT TITLE",
      width: "200px",
      headerWidth: "100%",
      filterable: true,
      cellStyle: "cellStyle",
    },
    {
      id: "type",
      label: "CONTENT TYPE",
      width: "200px",
      headerWidth: "100%",
      filterable: true,
    },
    {
      id: "status",
      label: "STATUS",
      width: "200px",
      cellStyle: "cellStyle",
      filterable: true,
    },
    {
      id: "truncatedSector",
      label: "SECTOR",
      width: "200px",
      filterable: true,
    },
    {
      id: "truncatedCountry",
      label: "COUNTRY",
      width: "200px",
      filterable: true,
    },
    { id: "language", label: "LANGUAGE", width: "200px", filterable: true },
    {
      id: "truncatedSubject",
      label: "SUBJECT",
      width: "200px",
      filterable: true,
    },
    { id: "actions", label: "ACTIONS", width: "80px", filterable: false },
  ];

  const renderTitleCell = (value, row) => {
    // Check if this is the title column by checking for fullTitle property
    if (row.fullTitle) {
      return <TitleWithTooltip value={value} fullTitle={row.fullTitle} />;
    }
    return value;
  };

  const renderActions = (row) => {
    switch (pageName) {
      case CURATOR_PAGE_API_CONSTANT.artificat_history:
        const isProcessingOrQueued = row?.processing || row?.queued;

        return (
          <Box
            sx={{
              display: "flex",
              gap: "10px",
              cursor: "pointer",
              alignItems: "center",
            }}
          >
            <Icon
              alt="delete"
              icon="delete"
              size="medium"
              disabled={isProcessingOrQueued}
              style={{ color: isProcessingOrQueued ? "#cccccc" : "#005cbc" }}
              onClick={(e) => {
                e.stopPropagation();
                if (isProcessingOrQueued) {
                  return;
                }
                handleActionClick(row, "delete");
              }}
            />
            {row.processing ? (
              <CircularProgress
                size={20}
                sx={{ color: "#005cbc" }}
                title="Processing..."
              />
            ) : row.queued ? (
              <Icon
                alt="queued"
                icon="schedule"
                size="medium"
                style={{ color: "#005cbc" }}
                title="Queued"
              />
            ) : (
              <Icon
                alt="edit"
                icon="edit"
                size="medium"
                style={{ color: "#005cbc" }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleNavigate(row);
                }}
              />
            )}
            <Icon
              alt="archive"
              icon="archive"
              size="medium"
              style={{ color: isProcessingOrQueued ? "#cccccc" : "#005cbc" }}
              disabled={isProcessingOrQueued}
              onClick={(e) => {
                e.stopPropagation();
                if (isProcessingOrQueued) {
                  return;
                }
                handleActionClick(row, "archive");
              }}
            />
          </Box>
        );

      case CURATOR_PAGE_API_CONSTANT.archives:
        return (
          <Icon
            alt="republish"
            icon="published_with_changes"
            size="medium"
            style={{ color: "#005cbc", cursor: "pointer" }}
            onClick={(e) => {
              e.stopPropagation();
              handleActionClick(row, "republish");
            }}
          />
        );

      default:
        return (
          <Typography
            sx={{
              color: "#005cbc",
              fontSize: "14px",
              fontWeight: 500,
              cursor: "pointer",
            }}
            onClick={(e) => {
              e.stopPropagation();
              handleNavigate(row);
            }}
          >
            EDIT
          </Typography>
        );
    }
  };

  return (
    <>
      {isModalOpen && (
        <ActionModal
          title={
            modalType === "republish"
              ? "Republish Content"
              : modalType === "archive"
              ? "Archive Content"
              : "Delete Content"
          }
          isOpen={isModalOpen}
          onClose={() => setModalOpen(false)}
          data={selectedRow}
          actionType={modalType}
          refreshData={refreshData}
        />
      )}
      <Box
        sx={{
          width: "100%",
          position: "relative",
          "& .MuiDataGrid-filler": { display: "none" },
          "& .MuiDataGrid-footerContainer": { display: "none" },
          "& .MuiDataGrid-virtualScroller": {
            overflowY: "hidden",
          },
        }}
        style={{ overflowY: "auto" }}
      >
        <DynamicTable
          rows={processedRows}
          columns={baseColumns}
          rowsPerPage={10}
          searchText={serachText}
          onRowClick={(row) => {
            console.log("Row clicked:", row);
          }}
          renderActions={(row) => renderActions(row)}
          cellRenderer={renderTitleCell}
          showTooltip={true}
          loading={loading}
        />
      </Box>
    </>
  );
};

export default DataTable;
